import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Hammer, Wrench, FileDown, Share2, Save, ShieldAlert, Package } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { vanDesignApi } from "@/api/vanDesign";
import { VanHeader, NCard, NBtn, MonoBadge } from "./vanUI";
import VanSchematic from "./VanSchematic";
import { diySteps, proSpec, buildSequence, catalogOf, tierOf, projectEstimate, diffLabel } from "./vanData";

export default function VanDesignBlueprint({ onBack }) {
  const { project, patch } = useVanDesign();
  const [tab, setTab] = useState("diy");
  const tierKey = project.goals.budget_tier;
  const tier = tierOf(tierKey);
  const modules = project.modules || [];

  const steps = useMemo(() => diySteps(modules, project.van, tierKey), [modules, project.van, tierKey]);
  const seq = useMemo(() => buildSequence(modules), [modules]);
  const estimate = projectEstimate(modules, tierKey);
  const complexity = Math.min(5, Math.round((modules.reduce((s, m) => s + (catalogOf(m.type)?.diff || 2), 0) / Math.max(1, modules.length))));

  const saveRevision = async () => {
    try {
      const saved = await vanDesignApi.saveProject({
        id: project.id, name: project.name, van: project.van, goals: project.goals, modules, ai: project.ai,
      });
      patch({ id: saved.id }); toast.success("Revision saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-blueprint">
      <VanHeader title="Blueprint" sub={`${tier.label} · ${modules.length} modules`} onBack={onBack} testid="van-blueprint-header" />

      {/* tabs */}
      <div className="shrink-0 grid grid-cols-2 gap-2 px-4 pt-3">
        {[["diy", "DIY Flat-Pack", Hammer], ["pro", "Pro Fabrication", Wrench]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`van-bp-tab-${k}`}
            className={`font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-xl border-2 border-black flex items-center justify-center gap-1.5 ${tab === k ? "bg-black text-[#FDFBF7] shadow-[3px_3px_0px_#000]" : "bg-white text-black"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-3">
        <NCard className="p-3 flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-black/60">Project estimate ({tier.label})</p>
            <p className="font-serif text-2xl text-black">${estimate.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <MonoBadge tone="gold">Complexity {complexity}/5</MonoBadge>
            <p className="font-mono text-[8px] text-black/50 mt-1">{tier.board}</p>
          </div>
        </NCard>

        {tab === "diy" ? (
          <>
            <NCard className="p-2.5 bg-[#FFF4D6] flex items-center gap-2">
              <Package size={14} /><p className="font-mono text-[9px] text-black/70">Progressive flat-pack install — each step keeps prior parts in place. <MonoBadge>preview</MonoBadge></p>
            </NCard>
            {steps.length === 0 && <NCard className="p-3"><p className="font-mono text-[11px] text-black/60">Add modules in the editor to generate assembly steps.</p></NCard>}
            {steps.map((s) => (
              <NCard key={s.n} className="p-3" data-testid={`van-diy-step-${s.n}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 grid place-items-center rounded-lg bg-black text-[#FDFBF7] font-mono font-black text-xs border-2 border-black">{s.n}</span>
                  <p className="font-serif text-base text-black flex-1">{s.title}</p>
                  <MonoBadge>{diffLabel(s.diff)}</MonoBadge>
                </div>
                {/* progressive mini schematic: prior installed + current highlighted */}
                <VanSchematic van={project.van}
                  modules={modules.filter((m) => s.installedIds.includes(m.id) || m.id === s.currentId)}
                  selectedId={s.currentId} editable={false} showGuides={false} maxW={340} maxH={230} onSelect={() => {}} />
                <div className="mt-2 space-y-1.5">
                  <p className="font-mono text-[10px] text-black/80 leading-relaxed">{s.action}</p>
                  <div className="flex flex-wrap gap-1">
                    <MonoBadge tone="dark">Parts</MonoBadge>
                    {s.parts.map((p, i) => <span key={i} className="font-mono text-[9px] text-black/70">{p}</span>)}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <MonoBadge tone="dark">Hardware</MonoBadge>
                    {s.hardware.map((h, i) => <span key={i} className="font-mono text-[9px] text-black/70">{h}{i < s.hardware.length - 1 ? " ·" : ""}</span>)}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <MonoBadge>Tools</MonoBadge>
                    {s.tools.map((t, i) => <span key={i} className="font-mono text-[9px] text-black/60">{t}{i < s.tools.length - 1 ? " ·" : ""}</span>)}
                  </div>
                  <div className="flex items-start gap-1.5 pt-1">
                    <ShieldAlert size={12} className="text-[#DC2626] shrink-0 mt-0.5" />
                    <p className="font-mono text-[9px] text-black/70">{s.safety}</p>
                  </div>
                </div>
              </NCard>
            ))}
            <NCard className="p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-1.5">Install readiness checklist</p>
              {["Van cleaned & lining fitted", "Furring strips / floor cleats in place", "Flat-pack parts unpacked & labelled", "12V power isolated before wiring", "Fixings & tools ready"].map((c, i) => (
                <label key={i} className="flex items-center gap-2 py-1"><span className="w-4 h-4 border-2 border-black rounded bg-white inline-block" /><span className="font-mono text-[10px] text-black/75">{c}</span></label>
              ))}
            </NCard>
          </>
        ) : (
          <>
            <NCard className="p-2.5 bg-[#E9F3FF] flex items-center gap-2">
              <Wrench size={14} /><p className="font-mono text-[9px] text-black/70">Fabrication-ready spec for a cabinet maker. Cut lists derive from the live layout. <MonoBadge>preview</MonoBadge></p>
            </NCard>
            {seq.map((m) => {
              const sp = proSpec(m, project.van, tierKey);
              return (
                <NCard key={m.id} className="p-3" data-testid={`van-pro-${m.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="font-serif text-base text-black">{sp.label}</p>
                    <MonoBadge tone="gold">${sp.cost.toLocaleString()}</MonoBadge>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-2">
                    {[["Footprint", sp.footprint], ["Material", sp.material], ["Thickness", sp.thickness], ["Finish", sp.finish], ["Joinery", sp.joinery], ["Tolerance", sp.tolerance]].map(([k, val]) => (
                      <div key={k} className="border-2 border-black rounded-lg p-1.5 bg-[#FDFBF7]">
                        <p className="font-mono text-[7px] uppercase text-black/50">{k}</p>
                        <p className="font-mono text-[9px] text-black font-bold leading-tight">{val}</p>
                      </div>
                    ))}
                  </div>
                  <p className="font-mono text-[8px] uppercase tracking-widest text-black/60 mb-1">Cut list</p>
                  <div className="border-2 border-black rounded-lg overflow-hidden mb-2">
                    {sp.cut.map((row, i) => (
                      <div key={i} className={`flex items-center justify-between px-2 py-1 ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                        <span className="font-mono text-[9px] text-black">{row.part}</span>
                        <span className="font-mono text-[9px] text-black/70">×{row.qty} · {row.size}mm</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <MonoBadge tone="dark">Hardware</MonoBadge>
                    {sp.hardware.map((h, i) => <span key={i} className="font-mono text-[9px] text-black/70">{h}{i < sp.hardware.length - 1 ? " ·" : ""}</span>)}
                  </div>
                  <p className="font-mono text-[9px] text-black/70 mt-1.5">Install: {sp.install}</p>
                </NCard>
              );
            })}
            <NCard className="p-3 bg-black text-[#FDFBF7]">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5">Flat-pack delivery</p>
              <p className="font-mono text-[10px] leading-relaxed text-[#FDFBF7]/85">
                Parts CNC-cut and edge-banded, labelled to match the DIY steps ({seq.map((m) => catalogOf(m.type)?.label?.split(" ")[0]?.toUpperCase()).slice(0, 4).join(" · ")}…),
                flat-packed per module with a hardware bag and fixing guide. Shop-ready: {complexity >= 4 ? "specialist" : "standard"} joinery.
              </p>
            </NCard>
          </>
        )}
        <div className="h-2" />
      </div>

      <div className="shrink-0 border-t-2 border-black p-3 bg-[#FDFBF7] grid grid-cols-3 gap-2">
        <NBtn variant="ghost" onClick={() => toast("PDF export coming soon — data is export-ready")} testid="van-export-pdf"><FileDown size={13} /> PDF</NBtn>
        <NBtn variant="ghost" onClick={() => toast("Share coming soon")} testid="van-share"><Share2 size={13} /> Share</NBtn>
        <NBtn variant="gold" onClick={saveRevision} testid="van-save-revision"><Save size={13} /> Save Rev</NBtn>
      </div>
    </div>
  );
}
